/**
 * Sistema de gestión de sesión robusto que sincroniza entre pestañas
 * Maneja cierre de sesión, inactividad y sincronización entre pestañas
 */

export interface SessionState {
  isAuthenticated: boolean;
  lastActivity: number;
  sessionId: string;
  userId?: string;
  expiresAt: number;
}

export class SessionManager {
  private static instance: SessionManager;
  private broadcastChannel: BroadcastChannel;
  private sessionStorageKey = 'app-session-state';
  private tabHeartbeatKey = 'app-tab-heartbeat';
  private static readonly TAB_HEARTBEAT_INTERVAL_MS = 5000; // Cada 5 segundos
  private static readonly TAB_HEARTBEAT_STALE_MS = 15000;   // Considerado muerto después de 15 segundos
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private tabHeartbeatInterval: NodeJS.Timeout | null = null;
  private inactivityTimeout: NodeJS.Timeout | null = null;
  private sessionTimeoutMinutes: number = 10;
  private callbacks: {
    onLogout: (() => void)[];
    onSessionExpired: (() => void)[];
    onSessionRestored: (() => void)[];
  } = {
    onLogout: [],
    onSessionExpired: [],
    onSessionRestored: []
  };

  private constructor() {
    this.broadcastChannel = new BroadcastChannel('session-sync');
    this.setupEventListeners();
    this.startHeartbeat();
  }

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  /**
   * Inicializa la sesión con la configuración específica
   */
  public initializeSession(sessionTimeoutMinutes: number = 10, userId?: string): void {
    this.sessionTimeoutMinutes = sessionTimeoutMinutes;
    
    const sessionState: SessionState = {
      isAuthenticated: true,
      lastActivity: Date.now(),
      sessionId: this.generateSessionId(),
      userId,
      expiresAt: Date.now() + (sessionTimeoutMinutes * 60 * 1000)
    };

    this.saveSessionState(sessionState);
    this.startTabHeartbeat();
    this.broadcastSessionChange('session-initialized', sessionState);
    this.resetInactivityTimer();
  }

  /**
   * Actualiza la actividad del usuario
   */
  public updateActivity(): void {
    const currentSession = this.getSessionState();
    if (!currentSession || !currentSession.isAuthenticated) return;

    const now = Date.now();
    const updatedSession: SessionState = {
      ...currentSession,
      lastActivity: now,
      expiresAt: now + (this.sessionTimeoutMinutes * 60 * 1000)
    };

    this.saveSessionState(updatedSession);
    this.broadcastSessionChange('activity-updated', updatedSession);
    this.resetInactivityTimer();
  }

  /**
   * Cierra la sesión de forma segura en todas las pestañas
   */
  public async logout(): Promise<void> {
    const currentSession = this.getSessionState();
    if (!currentSession) return;

    const loggedOutSession: SessionState = {
      ...currentSession,
      isAuthenticated: false,
      lastActivity: Date.now(),
      expiresAt: 0
    };

    this.saveSessionState(loggedOutSession);
    this.stopTabHeartbeat();
    this.broadcastSessionChange('logout', loggedOutSession);
    this.clearTimers();
    this.executeCallbacks('onLogout');
  }

  /**
   * Verifica si la sesión es válida
   */
  public isSessionValid(): boolean {
    const session = this.getSessionState();
    if (!session || !session.isAuthenticated) return false;

    const now = Date.now();
    return now < session.expiresAt;
  }

  /**
   * Obtiene el estado actual de la sesión
   */
  public getSessionState(): SessionState | null {
    try {
      const stored = localStorage.getItem(this.sessionStorageKey);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  /**
   * Registra callbacks para eventos de sesión
   */
  public onLogout(callback: () => void): void {
    this.callbacks.onLogout.push(callback);
  }

  public onSessionExpired(callback: () => void): void {
    this.callbacks.onSessionExpired.push(callback);
  }

  public onSessionRestored(callback: () => void): void {
    this.callbacks.onSessionRestored.push(callback);
  }

  /**
   * Limpia los callbacks
   */
  public clearCallbacks(): void {
    this.callbacks.onLogout = [];
    this.callbacks.onSessionExpired = [];
    this.callbacks.onSessionRestored = [];
  }

  /**
   * Validación SÍNCRONA al cargar la app.
   * Verifica si hay pestañas activas usando el heartbeat de localStorage.
   * 
   * Retorna:
   * - 'valid': sesión válida (refresh de pestaña o hay otra pestaña activa)
   * - 'expired': la sesión expiró por tiempo de inactividad
   * - 'closed': todas las pestañas fueron cerradas, sesión invalidada por seguridad
   * - 'none': no hay sesión almacenada
   */
  public validateSessionOnLoad(): 'valid' | 'expired' | 'closed' | 'none' {
    const session = this.getSessionState();

    // No hay sesión almacenada
    if (!session || !session.isAuthenticated) {
      return 'none';
    }

    // La sesión expiró por tiempo de inactividad
    if (Date.now() >= session.expiresAt) {
      this.invalidateStoredSession();
      return 'expired';
    }

    // Verificar si hay alguna pestaña activa via heartbeat
    if (this.isAnyTabAlive()) {
      // Hay al menos una pestaña activa (o es un refresh rápido) → sesión válida
      // Iniciar el heartbeat para esta nueva pestaña también
      this.startTabHeartbeat();
      return 'valid';
    }

    // No hay heartbeat reciente → todas las pestañas fueron cerradas → invalidar
    this.invalidateStoredSession();
    return 'closed';
  }

  /**
   * Verifica si hay alguna pestaña activa revisando el timestamp del heartbeat.
   * Es completamente síncrono.
   */
  private isAnyTabAlive(): boolean {
    try {
      const lastHeartbeat = localStorage.getItem(this.tabHeartbeatKey);
      if (!lastHeartbeat) return false;

      const timestamp = parseInt(lastHeartbeat, 10);
      if (isNaN(timestamp)) return false;

      return (Date.now() - timestamp) < SessionManager.TAB_HEARTBEAT_STALE_MS;
    } catch {
      return false;
    }
  }

  /**
   * Inicia el heartbeat de pestaña: escribe Date.now() en localStorage periódicamente.
   * Esto permite que al recargar o abrir una nueva pestaña se sepa que hay pestañas activas.
   */
  private startTabHeartbeat(): void {
    // Escribir heartbeat inmediatamente
    this.writeTabHeartbeat();

    // Limpiar intervalo anterior si existe
    if (this.tabHeartbeatInterval) {
      clearInterval(this.tabHeartbeatInterval);
    }

    // Escribir heartbeat periódicamente
    this.tabHeartbeatInterval = setInterval(() => {
      this.writeTabHeartbeat();
    }, SessionManager.TAB_HEARTBEAT_INTERVAL_MS);
  }

  /**
   * Detiene el heartbeat de pestaña y elimina el timestamp.
   */
  private stopTabHeartbeat(): void {
    if (this.tabHeartbeatInterval) {
      clearInterval(this.tabHeartbeatInterval);
      this.tabHeartbeatInterval = null;
    }
    try {
      localStorage.removeItem(this.tabHeartbeatKey);
    } catch {
      // Ignorar errores de localStorage
    }
  }

  /**
   * Escribe el timestamp actual como heartbeat.
   */
  private writeTabHeartbeat(): void {
    try {
      localStorage.setItem(this.tabHeartbeatKey, Date.now().toString());
    } catch {
      // Ignorar errores de localStorage
    }
  }

  /**
   * Invalida la sesión almacenada en localStorage.
   */
  private invalidateStoredSession(): void {
    const currentSession = this.getSessionState();
    if (!currentSession) return;

    const invalidSession: SessionState = {
      ...currentSession,
      isAuthenticated: false,
      expiresAt: 0
    };

    this.saveSessionState(invalidSession);
    this.stopTabHeartbeat();
  }

  /**
   * Destructor para limpiar recursos
   */
  public destroy(): void {
    this.clearTimers();
    this.stopTabHeartbeat();
    this.broadcastChannel.close();
    this.clearCallbacks();
    this.removeEventListeners();
  }

  // Métodos privados

  private setupEventListeners(): void {
    // Escuchar cambios desde otras pestañas
    this.broadcastChannel.addEventListener('message', (event) => {
      const { type, data } = event.data;
      
      switch (type) {
        case 'logout':
          this.handleLogoutFromOtherTab();
          break;
        case 'session-expired':
          this.handleSessionExpiredFromOtherTab();
          break;
        case 'activity-updated':
          this.handleActivityFromOtherTab(data);
          break;
        case 'session-initialized':
          this.handleSessionInitializedFromOtherTab(data);
          break;
      }
    });

    // Escuchar eventos de la ventana
    window.addEventListener('beforeunload', () => {
      this.handleBeforeUnload();
    });

    // Escuchar cuando la pestaña se vuelve visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.handleTabVisible();
      }
    });

    // Escuchar eventos de actividad
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      document.addEventListener(event, () => {
        this.updateActivity();
      }, { passive: true });
    });
  }

  private removeEventListeners(): void {
    // Remover eventos de actividad
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      document.removeEventListener(event, () => {
        this.updateActivity();
      });
    });
  }

  private handleLogoutFromOtherTab(): void {
    this.clearTimers();
    this.stopTabHeartbeat();
    this.executeCallbacks('onLogout');
  }

  private handleSessionExpiredFromOtherTab(): void {
    this.clearTimers();
    this.stopTabHeartbeat();
    this.executeCallbacks('onSessionExpired');
  }

  private handleActivityFromOtherTab(sessionData: SessionState): void {
    // Actualizar la sesión local con la actividad de otra pestaña
    const currentSession = this.getSessionState();
    if (currentSession && sessionData.lastActivity > currentSession.lastActivity) {
      this.saveSessionState(sessionData);
      this.resetInactivityTimer();
    }
  }

  private handleSessionInitializedFromOtherTab(sessionData: SessionState): void {
    // Si hay una sesión nueva, verificar si la actual es válida
    const currentSession = this.getSessionState();
    if (!currentSession || !currentSession.isAuthenticated) {
      this.executeCallbacks('onSessionRestored');
    }
  }

  private handleBeforeUnload(): void {
    // Opcional: Limpiar recursos antes de cerrar
    this.clearTimers();
  }

  private handleTabVisible(): void {
    // Cuando la pestaña se vuelve visible, verificar el estado de la sesión
    const session = this.getSessionState();
    if (session && session.isAuthenticated && !this.isSessionValid()) {
      this.expireSession();
    }
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.checkSessionStatus();
    }, 30000); // Verificar cada 30 segundos
  }

  private checkSessionStatus(): void {
    const session = this.getSessionState();
    if (!session || !session.isAuthenticated) return;

    if (!this.isSessionValid()) {
      this.expireSession();
    }
  }

  private expireSession(): void {
    const currentSession = this.getSessionState();
    if (!currentSession) return;

    const expiredSession: SessionState = {
      ...currentSession,
      isAuthenticated: false,
      expiresAt: 0
    };

    this.saveSessionState(expiredSession);
    this.stopTabHeartbeat();
    this.broadcastSessionChange('session-expired', expiredSession);
    this.clearTimers();
    this.executeCallbacks('onSessionExpired');
  }

  private resetInactivityTimer(): void {
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
    }

    this.inactivityTimeout = setTimeout(() => {
      this.expireSession();
    }, this.sessionTimeoutMinutes * 60 * 1000);
  }

  private clearTimers(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
      this.inactivityTimeout = null;
    }
  }

  private saveSessionState(session: SessionState): void {
    try {
      localStorage.setItem(this.sessionStorageKey, JSON.stringify(session));
    } catch (error) {
      console.error('Error saving session state:', error);
    }
  }

  private broadcastSessionChange(type: string, data: SessionState): void {
    try {
      this.broadcastChannel.postMessage({ type, data });
    } catch (error) {
      console.error('Error broadcasting session change:', error);
    }
  }

  private executeCallbacks(callbackType: keyof typeof this.callbacks): void {
    this.callbacks[callbackType].forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error(`Error executing ${callbackType} callback:`, error);
      }
    });
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Instancia singleton
export const sessionManager = SessionManager.getInstance();
